<script setup>
import { ref, computed, defineAsyncComponent, onMounted } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
/***/
import Str from '@classes/Str';
import User from '@classes/User';
/***/
import { useApplicationStore } from '@stores/application';
/***/
import { useAuthUser } from '@compositions/Auth';

// Composants
const Layout = defineAsyncComponent(() => import(/* webpackChunkName: "components" */ '@components/Layouts/Authenticated'));
const UserList = defineAsyncComponent(() => import(/* webpackChunkName: "components" */ '@components/Partial/Admin/Users/List/Wrapper'));
const Icon = defineAsyncComponent(() => import(/* webpackChunkName: "components" */ '@components/UI/Icon'));
const ExportCustomerListButton = defineAsyncComponent(() => import(/* webpackChunkName: "components" */ '@components/Partial/Admin/Users/List/ExportCustomerListButton'));

const route = useRoute();
const router = useRouter();
const { t } = useI18n();

// Store
const applicationStore = useApplicationStore();

// Application et utilisateur connecté
const currentApplication = computed(() => applicationStore.getCurrent);
const { authUser, authUserHasPermission } = useAuthUser({});

// Type d'utilisateurs à charger
const userTypeParam = route.params.userType;
const userTypeCamelCase = User.getCamelTypeBySlug(userTypeParam);
const userType = new Str(userTypeCamelCase).toSnakeCase('_').toUpperCase();

const allowed = ref(false);
const pageTitle = ref(null);
const pageDescription = ref(null);

onMounted(() => {

    const requiredPermission = User.getConsultPermissionByType(userTypeCamelCase);
    const canConsult = (requiredPermission !== null && authUserHasPermission(requiredPermission));

    if(! canConsult) {
        router.push({
            name: 'error',
            params: {
                statusCode: 404
            }
        });
    } else {
        const titleKey = 'page.admin.users.:type.list.title'.replace(':type', userTypeCamelCase);
        const descriptionKey = 'page.admin.users.:type.list.description'.replace(':type', userTypeCamelCase);
        document.title = t(titleKey) + ' - ' + currentApplication.value.name;
        pageTitle.value = t(titleKey);
        pageDescription.value = t(descriptionKey);
        allowed.value = true;
    }
});

// Vrai si l'utilisateur peut créer un utilisateur
const canCreateUser = computed(() =>
    userTypeCamelCase !== 'agencyManager' &&
    User.getCreatePermissionByType(userTypeCamelCase)
);

// Retourne les messages pour le label et title du bouton de création
const addButtonMessages = (() => {
    const keyLabel = 'page.admin.users.:userType.list.createUserButton.label'.replace(':userType', userTypeCamelCase);
    const keyAltLabel = 'page.admin.users.:userType.list.createUserButton.altLabel'.replace(':userType', userTypeCamelCase);
    return {
        label: t(keyLabel),
        altLabel: t(keyAltLabel)
    }
});
</script>

<template>
    <layout v-if="allowed">
        <div class="content_header">
            <div class="content-header_title">
                <h1 class="first-title">{{ pageTitle }}</h1>
                <p>{{ pageDescription }}</p>
            </div>
            <div class="content-header_btn">
                <router-link
                    v-if="canCreateUser"
                    :title="addButtonMessages().altLabel"
                    :to="{ name: 'admin.users.create', params: { userType: User.getSlugTypeByCamel(userTypeCamelCase) } }"
                    class="button button-primary-outline button-image button-icon"
                >
                    <icon name="plus"></icon>
                    <span>{{ addButtonMessages().label }}</span>
                </router-link>

                <export-customer-list-button
                    v-if="userType === 'CUSTOMER'"
                ></export-customer-list-button>
            </div>
        </div>

        <user-list :user-type="userType"></user-list>
    </layout>
</template>
